'use client'

import React, { ReactNode, useId } from 'react'

export interface LoanFamilyQuestion {
    id: string
    kind: 'select' | 'switch'
    node: ReactNode
    /** Question complémentaire : s'ouvre sous sa question mère, dans sa colonne. */
    child?: ReactNode
}

interface LoanFieldFamilyProps {
    title: string
    hint?: string
    tone?: 'white' | 'cream'
    rule?: boolean
    columns?: 2 | 3
    questions: LoanFamilyQuestion[]
    className?: string
}

const GRID = 'grid grid-cols-1 items-start gap-y-4 md:gap-x-[40px] md:gap-y-[24px]'

function QuestionCell({ question, fullWidth }: { question: LoanFamilyQuestion; fullWidth: boolean }) {
    const cellClass = fullWidth ? 'md:col-span-2' : ''

    if (!question.child) {
        return <div className={cellClass}>{question.node}</div>
    }

    return (
        <div className={cellClass}>
            <div className="[--rm-switch-track:#fff] rounded-r-lg border-l-2 border-rm-blue bg-rm-blue-pale pl-3.5 md:-mt-2.5">
                <div className="py-2.5">{question.node}</div>
                <div className="border-t border-rm-blue-mist py-2.5">{question.child}</div>
            </div>
        </div>
    )
}

export default function LoanFieldFamily({
    title,
    hint,
    tone = 'white',
    rule = false,
    columns = 2,
    questions,
    className = '',
}: LoanFieldFamilyProps) {
    const titleId = useId()

    if (questions.length === 0) return null

    const isCream = tone === 'cream'
    // No bleed: the fill and the rule stop at the field edge of the family above
    const toneClass = isCream
        ? '[--rm-switch-track:#fff] rounded-xl bg-rm-cream p-3 md:p-5'
        : rule
          ? 'border-t border-rm-gray-rule pt-4 md:pt-5'
          : ''

    const selects = questions.filter((question) => question.kind === 'select')
    const switches = questions.filter((question) => question.kind === 'switch')
    // Un menu déroulant ne prend la pleine largeur que s'il est le seul de sa famille.
    const isFullWidth = (question: LoanFamilyQuestion) => selects.length === 1 && question.kind === 'select'

    const renderGrid = (group: LoanFamilyQuestion[], gridColumns: number) => (
        // Trois colonnes seulement à partir de xl : en dessous le libellé du statut le plus long est tronqué
        <div className={`${GRID} ${gridColumns === 3 ? 'md:grid-cols-2 xl:grid-cols-3' : 'md:grid-cols-2'}`}>
            {group.map((question) => (
                <QuestionCell
                    key={question.id}
                    question={question}
                    fullWidth={gridColumns === 2 && group.length === 1 && isFullWidth(question)}
                />
            ))}
        </div>
    )

    // 2 questions ou moins : ordre naturel. Au-delà : les déroulants d'abord, puis les switchs.
    const splitByKind = columns === 2 && questions.length > 2

    return (
        <section role="group" aria-labelledby={titleId} className={`${toneClass} ${className}`}>
            <p id={titleId} className="m-0 mb-2.5 text-[13px] font-bold text-pale md:mb-3.5">
                {title}
                {hint && <span className="ml-2 hidden font-normal text-pale/50 md:inline">{hint}</span>}
            </p>
            {splitByKind ? (
                <div className="flex flex-col gap-y-4 md:gap-y-[24px]">
                    {selects.length > 0 && renderGrid(selects, 2)}
                    {switches.length > 0 && renderGrid(switches, 2)}
                </div>
            ) : (
                renderGrid(questions, columns)
            )}
        </section>
    )
}
